import React from 'react';

import { H4, H5, H6, H7 } from '../Typography';

import { Slot } from './Slot';
import type { InputWrapperOwnProps, InputWrapperProps } from './types';

/**
 * Get the main typography for the input (used for the label and the input)
 * @param size - The size of the input
 */
export const getInputMainTypography = (size: InputWrapperProps['size']) => {
  switch (size) {
    case 's-condensed':
      return H7;
    case 's':
      return H6;
    case 'l':
      return H4;
    case 'm':
    default:
      return H5;
  }
};

/**
 * Exclude the wrapper props from the input props to avoid passing invalid props to the input
 */
export const excludeWrapperProps = <T extends InputWrapperProps>(
  props: T
): Omit<T, keyof InputWrapperOwnProps> => {
  const {
    size: _,
    state: __,
    label: ___,
    disclaimer: ____,
    leftIcon: _____,
    rightIcon: ______,
    dataTestId: _______,
    ...rest
  } = props;
  return rest as Omit<T, keyof InputWrapperOwnProps>;
};

/**
 * RootWrapper only accepts children of other components that are exported from this file
 * to ensure proper styling and behavior
 */
const validChildren: React.ElementType[] = [Slot];

export const validateChildren = (children: React.ReactNode) => {
  React.Children.forEach(children, (child) => {
    if (child === null) {
      return;
    }

    if (!React.isValidElement(child) || typeof child.type === 'string') {
      throw new Error(
        'Input children must be a valid React element if set (e.g. <Slot>...</Slot>)'
      );
    }

    if (!validChildren.includes(child.type)) {
      throw new Error('Input children must be a wrapper in a Slot component');
    }
  });
};
